import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';

import {
    DataTable,
    type TableColumn,
    EditIcon,
    ActivateIcon,
    DeactivateIcon,
    ICON_COLORS,
} from '@/src/ui';
import { type Option, ReportHeaderStyle, ReportCellStyle } from '@/src/utils';
import type { QuizListItemDTO } from '../models/quizDTOs';

interface QuizzesTableProps {
    data: QuizListItemDTO[];
    gradeOptions: Option[];
    searchString: string;
    onShare: (quiz: QuizListItemDTO) => void;
    onPublishToggle: (quiz: QuizListItemDTO) => void;
}

export default function QuizzesTable({
    data,
    gradeOptions,
    searchString,
    onShare,
    onPublishToggle,
}: QuizzesTableProps) {
    const filteredData = useMemo(() => {
        const search = searchString.trim().toLowerCase();
        if (!search) return data;
        return data.filter((q) => q.title.trim().toLowerCase().includes(search));
    }, [data, searchString]);

    const gradeNameById = useMemo(() => {
        const map: Record<number, string> = {};
        gradeOptions.forEach((g) => {
            map[Number(g.id)] = g.name;
        });
        return map;
    }, [gradeOptions]);

    const columns = useMemo<TableColumn[]>(() => {
        return [
            {
                dataKey: 'title',
                header: 'Title',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'intendedGrades',
                header: 'Intended Grades',
                align: 'left',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: QuizListItemDTO) =>
                    row.intendedGrades.map((id) => gradeNameById[id] || `#${id}`).join(', ') ||
                    '<No Grades>',
            },
            {
                dataKey: 'actions',
                header: 'Actions',
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: QuizListItemDTO) =>
                    row.hasEditAccess ? (
                        <>
                            <Tooltip title="Edit" arrow>
                                <Link
                                    to={`/exams/dynamicQuizGenerator?viewMode=edit&resourceId=${row.id}`}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0.6em 1.2em',
                                    }}
                                >
                                    <span role="img" aria-label="edit">
                                        <EditIcon />
                                    </span>
                                </Link>
                            </Tooltip>
                            <Tooltip title={row.isPublished ? 'Published' : 'Not Published'} arrow>
                                <button
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0.6em 1.2em',
                                    }}
                                    onClick={() => onPublishToggle(row)}
                                >
                                    <span
                                        role="img"
                                        aria-label={row.isPublished ? 'unpublish' : 'publish'}
                                    >
                                        {row.isPublished ? (
                                            <ActivateIcon color="green" />
                                        ) : (
                                            <DeactivateIcon color="red" />
                                        )}
                                    </span>
                                </button>
                            </Tooltip>
                            <Tooltip title="Share" arrow>
                                <button
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0.6em 1.2em',
                                        color: ICON_COLORS.primary,
                                    }}
                                    onClick={() => onShare(row)}
                                >
                                    <span role="img" aria-label="share">
                                        <ShareIcon
                                            sx={{ width: 20, height: 20, display: 'block' }}
                                            htmlColor={ICON_COLORS.primary}
                                        />
                                    </span>
                                </button>
                            </Tooltip>
                        </>
                    ) : (
                        <div>View Only ({row.isPublished ? 'Published' : 'Not Published'})</div>
                    ),
            },
        ];
    }, [gradeNameById, onShare, onPublishToggle]);

    return (
        <DataTable
            data={filteredData}
            columns={columns}
            tableName="YourQuizzes"
            alternatingRowColor
            defaultSortColumn="title"
            defaultSortDirection="asc"
        />
    );
}
